/**
 * Cutoff Countdown - hardlocked to Europe/Warsaw timezone.
 * Renders "Order in {time} for shipping today" before cutoff, "Ships tomorrow" if next
 * business day is the very next calendar day, otherwise "Ships {weekday}".
 *
 * Cutoff hour, holidays list come from data-attrs set by cutoff-countdown.liquid
 * (configured per-template in the buy_buttons block schema).
 * Saturdays + Sundays always excluded.
 * Updates every minute (not second) for battery/perf. Pauses when tab hidden.
 */

const WARSAW_TZ = 'Europe/Warsaw';
const TICK_INTERVAL_MS = 60 * 1000;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseHolidays(csv) {
    if (!csv) return new Set();
    return new Set(
        csv.split(',')
            .map(s => s.trim())
            .filter(s => ISO_DATE_RE.test(s))
    );
}

function warsawDateParts(date) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: WARSAW_TZ,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'short',
    }).formatToParts(date);
    return parts.reduce((acc, p) => {
        if (p.type !== 'literal') acc[p.type] = p.value;
        return acc;
    }, {});
}

function warsawOffsetString(date) {
    const longOffset = new Intl.DateTimeFormat('en-GB', {
        timeZone: WARSAW_TZ,
        timeZoneName: 'longOffset',
    }).formatToParts(date).find(p => p.type === 'timeZoneName')?.value || 'GMT+00:00';
    return longOffset.replace('GMT', '') || '+00:00';
}

function warsawCutoffDate(date, hour) {
    const p = warsawDateParts(date);
    const offset = warsawOffsetString(date);
    const hh = String(hour).padStart(2, '0');
    return new Date(`${p.year}-${p.month}-${p.day}T${hh}:00:00${offset}`);
}

function isBusinessDayWarsaw(date, holidays) {
    const p = warsawDateParts(date);
    if (p.weekday === 'Sat' || p.weekday === 'Sun') return false;
    const iso = `${p.year}-${p.month}-${p.day}`;
    return !holidays.has(iso);
}

function nextBusinessDayWarsaw(from, holidays) {
    let d = new Date(from.getTime() + 24 * 3600 * 1000);
    let safety = 21;
    while (!isBusinessDayWarsaw(d, holidays) && safety-- > 0) {
        d = new Date(d.getTime() + 24 * 3600 * 1000);
    }
    return d;
}

function warsawIsoDate(date) {
    const p = warsawDateParts(date);
    return `${p.year}-${p.month}-${p.day}`;
}

class CutoffCountdown extends HTMLElement {
    connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        const serverNowRaw = this.dataset.serverNow;
        const serverNow = serverNowRaw ? new Date(serverNowRaw) : new Date();
        this.skew = isNaN(serverNow.getTime()) ? 0 : serverNow.getTime() - Date.now();

        this.cutoffHour = parseInt(this.dataset.cutoffHour, 10) || 14;
        this.locale = this.dataset.locale || document.documentElement.lang || 'en';
        this.holidays = parseHolidays(this.dataset.holidays || '');
        this.labelEl = this.querySelector('.cutoff-countdown__label');
        this.strings = window.enrageCutoffStrings || {
            ships_today: 'Order in {time} for shipping today',
            ships_tomorrow: 'Ships tomorrow',
            ships_next: 'Ships {day}',
            h_short: 'h',
            min_short: 'min',
        };

        this._tickBound = () => this.tick();
        this._visBound = () => this.handleVisibility();

        this.tick();
        this.interval = setInterval(this._tickBound, TICK_INTERVAL_MS);
        document.addEventListener('visibilitychange', this._visBound);
    }

    disconnectedCallback() {
        clearInterval(this.interval);
        document.removeEventListener('visibilitychange', this._visBound);
    }

    handleVisibility() {
        if (document.hidden) {
            clearInterval(this.interval);
        } else {
            this.tick();
            this.interval = setInterval(this._tickBound, TICK_INTERVAL_MS);
        }
    }

    nowWarsaw() {
        return new Date(Date.now() + this.skew);
    }

    formatRemaining(diffMs) {
        const totalMin = Math.max(0, Math.floor(diffMs / 60000));
        const h = Math.floor(totalMin / 60);
        const m = totalMin % 60;
        if (h > 0) return `${h}${this.strings.h_short} ${m}${this.strings.min_short}`;
        return `${m}${this.strings.min_short}`;
    }

    tick() {
        if (!this.labelEl) return;
        const now = this.nowWarsaw();
        const isToday = isBusinessDayWarsaw(now, this.holidays);

        if (isToday) {
            const cutoff = warsawCutoffDate(now, this.cutoffHour);
            const diff = cutoff.getTime() - now.getTime();
            if (diff > 0) {
                const timeStr = this.formatRemaining(diff);
                this.labelEl.textContent = this.strings.ships_today.replace('{time}', timeStr);
                this.dataset.state = 'before-cutoff';
                return;
            }
        }

        const next = nextBusinessDayWarsaw(now, this.holidays);
        const tomorrow = new Date(now.getTime() + 24 * 3600 * 1000);
        const isTomorrow = warsawIsoDate(next) === warsawIsoDate(tomorrow);

        if (isTomorrow) {
            this.labelEl.textContent = this.strings.ships_tomorrow;
            this.dataset.state = 'ships-tomorrow';
            return;
        }

        let dayStr;
        try {
            dayStr = new Intl.DateTimeFormat(this.locale, {
                weekday: 'long',
                timeZone: WARSAW_TZ,
            }).format(next);
        } catch (e) {
            dayStr = new Intl.DateTimeFormat('en', { weekday: 'long', timeZone: WARSAW_TZ }).format(next);
        }
        this.labelEl.textContent = this.strings.ships_next.replace('{day}', dayStr);
        this.dataset.state = 'after-cutoff';
    }
}

if (!customElements.get('cutoff-countdown')) {
    customElements.define('cutoff-countdown', CutoffCountdown);
}
